import { ClientQuery, PolicyCreateDto } from '@bom-nextgen-keycloak/models';
import {
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  createPolicy,
  ErrorMessage,
  fetchClients,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  AutoCompleteOption,
  DropdownField,
  StyledDialogTitle,
  TextField,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  MESSAGE_NO_BEGINNING_SPACE_REGEX,
  NO_BEGINNING_SPACE_REGEX,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import {
  CircularProgress,
  MenuItem,
  TextField as MatTextField,
} from '@mui/material';
import Autocomplete, {
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { FC, Fragment, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { TableForm } from '../TableForm';
import { StyledDialog } from './ClientFormDialog.styled';

type ClientFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
};

type ClientForm = {
  name: string;
  description: string;
  type: string;
  logic: string;
  clientId: AutoCompleteOption[];
};

const validationSchema = yup.object({
  name: yup
    .string()
    .matches(NO_BEGINNING_SPACE_REGEX, MESSAGE_NO_BEGINNING_SPACE_REGEX)
    .required('Name is required'),
  clientId: yup.array().min(1, 'Clients is required'),
  logic: yup.string().required('Logic is required'),
});

const initialValues: ClientForm = {
  name: '',
  description: '',
  clientId: [],
  logic: 'POSITIVE',
  type: 'client',
};

const ClientFormDialog: FC<ClientFormDialogProps> = ({
  onClose,
  onSave,
  open,
}) => {
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const resource = useResource();

  const [params] = useState<ClientQuery>({
    search: true,
    clientId: '',
    first: 0,
    max: 25,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQueryTerm = useDebounce(searchQuery, 700);

  const { isLoading, data: optionClient = [] } = useQuery(
    [QUERY_KEY.CLIENT_LIST, params, debouncedQueryTerm],
    async () => {
      const clients = await fetchClients({
        ...params,
        clientId: searchQuery,
      });
      return clients.map((client) => ({
        label: client.name || '',
        value: client,
      }));
    }
  );

  const onInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setSearchQuery(value);
  };

  const createPolicyMutation = useMutation(createPolicy, {
    onSuccess: () => {
      setNotification({
        message: 'Client-based policy created',
        type: 'success',
      });
      onSave();
    },
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message || 'Cannot created policy. try again';
      setAlertMessage({
        message:
          error.response?.status === 409
            ? 'Client name already exists. try another name'
            : message,
        typeStatusMessage: 'error',
        statusCode: error.response?.status,
      });
    },
  });

  const handleDelateClient = (
    data: AutoCompleteOption,
    setFieldValue: (field: string, value: any) => void,
    clientIds: AutoCompleteOption[]
  ) => {
    if (data) {
      const newClient = clientIds.filter(
        (val) => val.value.id !== data.value.id
      );
      setFieldValue('clientId', newClient);
    }
  };

  return (
    <StyledDialog
      onClose={() => onClose()}
      aria-labelledby="dialog-title"
      open={open}
    >
      <StyledDialogTitle id="dialog-title">
        Add client-based policy
      </StyledDialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={(
          values: ClientForm,
          { setSubmitting }: FormikHelpers<ClientForm>
        ) => {
          const payload: PolicyCreateDto = {
            logic: values.logic,
            name: values.name,
            description: values.description,
            type: values.type,
            clients: values.clientId.map((val) => val.value?.id || ''),
          };
          createPolicyMutation.mutate({ clientId: resource.clientId, payload });
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
          values,
          getFieldMeta,
          setFieldTouched,
        }) => (
          <Form>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <TextField name="name" label="Name" required />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField name="description" label="Description" multiline />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  id="policy"
                  filterSelectedOptions
                  options={optionClient}
                  multiple
                  getOptionLabel={(option) => option.label}
                  limitTags={3}
                  value={values.clientId}
                  onChange={(e, value) => {
                    setFieldValue('clientId', value || []);
                  }}
                  onInputChange={onInputChange}
                  loading={isLoading}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  renderInput={(inputParams) => (
                    <MatTextField
                      {...inputParams}
                      id="clientId"
                      name="clientId"
                      label="Clients"
                      fullWidth
                      placeholder="Enter client name"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="standard"
                      required={values.clientId.length === 0}
                      onBlur={() => {
                        setFieldTouched('clientId');
                      }}
                      error={
                        getFieldMeta('clientId').touched &&
                        !!getFieldMeta('clientId').error
                      }
                      helperText={
                        getFieldMeta('clientId').touched &&
                        getFieldMeta('clientId').error
                      }
                      InputProps={{
                        ...inputParams.InputProps,
                        endAdornment: (
                          <Fragment>
                            {isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {inputParams.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
              {values.clientId.length !== 0 && (
                <Box sx={{ mb: 2 }}>
                  <TableForm
                    columnName="Name"
                    tooltipTitle="Remove client"
                    data={values.clientId}
                    onClickDelate={(data) =>
                      handleDelateClient(data, setFieldValue, values.clientId)
                    }
                  />
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <DropdownField
                  name="logic"
                  label="Logic"
                  placeholder="please enter your logic"
                  required
                  variant="standard"
                >
                  <MenuItem value="POSITIVE">Positive</MenuItem>
                  <MenuItem value="NEGATIVE">Negative</MenuItem>
                </DropdownField>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                color="secondary"
                onClick={() => onClose()}
                variant="text"
              >
                Cancel
              </Button>
              <LoadingButton
                disabled={!(isValid && dirty) || isSubmitting}
                loading={createPolicyMutation.isLoading}
                type="submit"
                variant="text"
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};

export { ClientFormDialog };
